import { API, Storage } from 'aws-amplify';
import { cloneDeep, get } from 'lodash';
import html2canvas from 'html2canvas';
import JSPdf from 'jspdf';

import { fetchAIForm } from 'src/apis';
import { defaultFieldProps, fullFieldProps, countryOptions, usStateOptions } from 'src/constants';
import { FormFieldType, ISummary, TAIForm, TAIFormMaterial, TCustomField, TRequest, IMaterial } from 'src/types';
import { processResponse } from './api';

import { formatDate, getJoinedDate, parseDate } from './day';
import { formatString } from './string';
import { downloadCSV } from './csv';
import { buildBatches } from 'src/services/schoolRequest';

const MARGIN = 10;

export const getUpdatedAIFormValues = (school: any): any => {
  const values = cloneDeep(school);
  const {
    schoolEstMonth,
    schoolEstDay,
    schoolEstYear,
    approvedAccreditation: { cbAccreditedMonth, cbAccreditedDay, cbAccreditedYear },
    administeredTest: { administeredTestMonth, administeredTestDay, administeredTestYear },
  } = school;

  values.schoolEstDate = getJoinedDate(schoolEstMonth, schoolEstDay, schoolEstYear) || parseDate(values.schoolEstDate);

  values.approvedAccreditation.cbAccreditedDate =
    getJoinedDate(cbAccreditedMonth, cbAccreditedDay, cbAccreditedYear) ||
    parseDate(values.approvedAccreditation.cbAccreditedDate);

  values.administeredTest.administeredTestDate =
    getJoinedDate(administeredTestMonth, administeredTestDay, administeredTestYear) ||
    parseDate(values.administeredTest.administeredTestDate || values.administeredTest.administeredtestDate);

  return values;
};

export const getValidatedSchoolName = (schoolName: string): any => {
  //replace greater than symbol ,less than symbol in school name with single space
  const schoolNameWithoutAngleBrackets = schoolName.replace(/[<>]+/g, ' ');

  // for single and double quotes in school name, we need to remove them and NOT replace with single space
  const schoolNameWithoutQuotes = schoolNameWithoutAngleBrackets.replace(/['"]+/g, '');

  return schoolNameWithoutQuotes;
};

export const getAIFormRequestData = (formData: any, userName: string, userEmail: string): any => {
  try {
    const accrExpirationDate = new Date(formData.approvedAccreditation.cbAccreditedDate);
    const today = new Date(Date.now());
    const numDaysDiff = Math.ceil((accrExpirationDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
    if (numDaysDiff < 31) {
      formData.accreditationExpiry = 'Y';
    } else {
      formData.accreditationExpiry = 'N';
    }
  } catch (e) {
    formData.accreditationExpiry = 'Y';
  }

  formData.creator = userName;
  formData.creatorEmail = userEmail;

  if (formData.processStatus === 'reopen') {
    formData.processStatus = 'processing';
  } else {
    formData.processStatus = 'submitted';
  }
  return formData;
};

export const uploadSchoolFile = async (user: string, school: ISummary | TAIForm, file: File, description: string, path?: string) => {
  const contentType = file.type;
  let fileName = file.name;

  if (!contentType || (contentType !== 'application/pdf' && !contentType.startsWith('image'))) {
    throw new Error('You can only upload images or PDF files.');
  }

  fileName = fileName.replace(/[\s,//]+/g, '');
  if (fileName.length > 200) {
    fileName = fileName.substr(0, 200);
  }

  try {
    const result = await Storage.put(school.categoryId + '/support/' + fileName, file, {
      level: 'private',
      contentType: contentType,
    });

    await API.post('AiFormRequest', '/save?key=support', {
      body: {
        ...school,
        user: user,
        s3key: result.key,
        fieldPath: path,
        fileName,
        contentType,
        description: !!description ? description : undefined,
      },
    }).then((response) => processResponse(response));

    return result.key;
  } catch (error: any) {
    throw new Error('Failed to upload file: ' + error.message);
  }
};

export const getFileLink = async (data: IMaterial) => {
  return await API.post('AiFormRequest', '/save?key=getFileLink', {
    body: {
      filePath: data.filePath
    },
  }).then((response) => processResponse(response));
};

export const uploadFile = async (user: string, school: Partial<ISummary>, file: File, description?: string, path?: string, multi?: boolean): Promise<IMaterial> => {
  const contentType = file.type;
  let fileName = file.name;

  if (!contentType || (contentType !== 'application/pdf' && !contentType.startsWith('image'))) {
    throw new Error('You can only upload images or PDF files.');
  }

  fileName = fileName.replace(/[\s,//]+/g, '');
  if (fileName.length > 200) {
    fileName = fileName.substr(0, 200);
  }

  try {
    const result = await Storage.put(school.categoryId + '/support/' + fileName, file, {
      level: 'private',
      contentType: contentType,
    });

    const record: IMaterial = await API.post('AiFormRequest', '/save?key=support', {
      body: {
        ...school,
        user: user,
        s3key: result.key,
        fieldPath: path,
        fileName,
        contentType,
        description,
        multi
      }
    }).then((response) => processResponse(response));
    return record;
  } catch (error: any) {
    const type = typeof error;
    let message = 'Unknown error occurred uploading the file';
    if (type === 'object') {
      message = error.message || message;
    } else if (type === 'string') {
      message = error;
    } else {
      console.log(error);
    }
    throw new Error(`Failed to upload file: ${message}`);
  }
};

export const generatePDF = async (printSections: HTMLDivElement[], schoolName: string) => {
  const results = await Promise.all(
    printSections.map(async (section) => {
      const canvas = await html2canvas(section, { logging: false });
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      return {
        imageData: canvas,
        width: imgWidth,
        height: imgHeight,
      };
    }),
  );

  const doc = new JSPdf();
  const fileName = schoolName + '.pdf';
  let pageY = MARGIN;

  results.forEach((image) => {
    if (!image.height || !image.width) {
      return;
    }

    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    if (pageY + image.height > pageHeight - MARGIN) {
      pageY = MARGIN;
      if (image.height <= pageHeight) {
        doc.addPage();
      }
    }

    doc.addImage(image.imageData, 'PNG', MARGIN, pageY, pageWidth - 2 * MARGIN, image.height);
    pageY += image.height;
  });

  doc.save(fileName);
};

export const formatProcessStatus = (status: TAIForm['processStatus']) => {
  return {
    started: 'Started',
    submitted: 'Submitted',
    reopen: 'Reopened',
    processing: 'Under Review',
    completed: 'Completed',
    appeal: 'Pending Appeal',
    international: 'Pending International',
    testSecurity: 'Pending Test Security',
    opsApproved: 'Vetting Approved',
    rejected: 'Rejected',
    xpmDenied: 'XPM Denied',
    deactivationApproval: 'Deactivation Approval'
  }[status];
};

export const formatMaterialList = (materials: TAIFormMaterial[]) => {
  return materials.map((material) => material.fileName).join(', ');
};

export const convertMaterialList = (materials: IMaterial[]) => {
  return materials.map((material) => material.fileName).join(', ');
};

export const buildCSV = async (summaries: ISummary[]) => {
  const mapped = summaries.map((summary) => {
    if (!summary.submittedDate || !summary.submissionDate) {
      return null;
    }

    return {
      'Initial Submit Date': formatDate(summary.submittedDate),
      'Last Submit Date': formatDate(summary.submissionDate),
      'Last Update Date': formatDate(summary.updateDate),
      'Submitted by': summary.creator,
      'Email': summary.creatorEmail,
      'School Name': summary.schoolName,
      'Process Status': formatProcessStatus(summary.processStatus),
      'Assigned AI Code': summary.aiCode ?? '',
      'Material List': convertMaterialList(summary.materials),
      'Applied Level': summary.appliedLevel ?? '',
      'Applied Grade Level': summary.schoolType === 'hs' ? 'High School' : 'MiddleSchool', //Maybe we need to move this too to dynamo
      'Assigned AI Level': summary.aiLevelInd ?? ''
    };
  });

  const csvHeaders = [
    'Initial Submit Date',
    'Last Submit Date',
    'Last Update Date',
    'Submitted by',
    'Email',
    'School Name',
    'Process Status',
    'Applied Level',
    'Applied Grade Level',
    'Assigned AI Code',
    'Assigned AI Level',
    'Material List',
  ];

  const csvData = mapped
    .filter((each) => each)
    .map((row) => csvHeaders.map((fieldName) => {
      const value: string = get(row, fieldName, '');
      if (typeof value === 'string') {
        return formatString(value);
      } else {
        console.log(`Field wasn't a string: '${fieldName}'`, value);
        return '';
      }
    }).join(','))
    .join('\n');

  downloadCSV({
    headers: csvHeaders,
    data: csvData,
    fileName: '',
  });
};

const mapExport = async (request: TRequest) => {
  const aiForm = await fetchAIForm(request.link);

  if (!request.detail) {
    return null;
  }

  if (!request.detail?.submits?.length) {
    return null;
  }

  return {
    'Initial Submit Date': formatDate(request.submittedTime),
    'Last Submit Date': formatDate(request.detail.submits[0].updateDate),
    'Last Update Date': formatDate(request.detail.updateDate),
    'Submitted by': request.detail.creator,
    'Email': request.detail.creatorEmail,
    'School Name': request.detail.schoolName,
    'Process Status': formatProcessStatus(request.detail.processStatus),
    'Assigned AI Code': aiForm.aiCode ?? '',
    'Material List': formatMaterialList(request.detail.materials),
    'Applied Level': aiForm.level ?? '',
    'Applied Grade Level': aiForm.schoolType === 'hs' ? 'High School' : 'MiddleSchool',
  };
};

export const generateCSV = async (requests: TRequest[]) => {
  if (!requests.length) {
    return;
  }

  // Create a CSV final array
  let exportData: any = [];

  // Convert the requests into batches
  const batches = buildBatches<TRequest>(requests, 20);
  for (const batch of batches) {
    const batchResult = await Promise.all(batch.map(mapExport));
    exportData = exportData.concat(batchResult);
  }

  const csvHeaders = [
    'Initial Submit Date',
    'Last Submit Date',
    'Last Update Date',
    'Submitted by',
    'Email',
    'School Name',
    'Process Status',
    'Applied Level',
    'Applied Grade Level',
    'Assigned AI Code',
    'Material List',
  ];

  const csvData = exportData
    .filter((each: any) => each)
    .map((row: any) => csvHeaders.map((fieldName) => formatString(get(row, fieldName, ''))).join(','))
    .join('\n');

  downloadCSV({
    headers: csvHeaders,
    data: csvData,
    fileName: '',
  });
};

export const getAddressFields = (prefix: string): TCustomField[] => {
  return [
    {
      label: 'Address 1:',
      comment: 'P.O. Box addresses are not acceptable',
      path: `${prefix}.address1`,
      placeholder: 'Street Address',
      type: FormFieldType.TEXT,
      required: true,
      ...fullFieldProps,
    },
    {
      label: 'Address 2:',
      path: `${prefix}.address2`,
      placeholder: 'Street Address',
      type: FormFieldType.TEXT,
      ...fullFieldProps,
    },
    {
      label: 'Address 3:',
      path: `${prefix}.address3`,
      placeholder: 'Street Address',
      type: FormFieldType.TEXT,
      ...fullFieldProps,
    },
    {
      label: 'City:',
      path: `${prefix}.city`,
      placeholder: 'City',
      type: FormFieldType.TEXT,
      required: true,
      ...defaultFieldProps,
    },
    {
      label: 'County:',
      path: `${prefix}.county`,
      placeholder: 'County',
      type: FormFieldType.TEXT,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country === 'US' || field[prefix]?.country === '',
    },
    {
      label: 'State/Province:',
      path: `${prefix}.stateOrProvince`,
      placeholder: 'State/Province',
      type: FormFieldType.TEXT,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country !== 'US' && field[prefix]?.country !== '',
    },
    {
      label: 'Country:',
      path: `${prefix}.country`,
      placeholder: 'Select One',
      type: FormFieldType.SELECT,
      options: countryOptions,
      required: true,
      ...defaultFieldProps,
    },
    {
      label: 'State:',
      path: `${prefix}.state`,
      placeholder: 'Select One',
      type: FormFieldType.SELECT,
      options: usStateOptions,
      ...defaultFieldProps,
      required: true,
      renderCondition: (field) => field[prefix]?.country === 'US' || field[prefix]?.country === '',
    },
    {
      label: 'Zip:',
      maxLength: 5,
      path: `${prefix}.zip`,
      placeholder: '5 Digit Zip Code',
      type: FormFieldType.TEXT,
      required: true,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country === 'US' || field[prefix]?.country === '',
    },
    {
      label: 'International Postal Code:',
      path: `${prefix}.internationalPostalCode`,
      placeholder: 'International Postal Code',
      type: FormFieldType.TEXT,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country !== 'US' && field[prefix]?.country !== '',
    },
  ];
};
